import { useCallback, useEffect, useRef, useState } from "react";
import * as FormValidationService from "../services/formValidationService";
import * as QuestionValidationService from "../services/questionValidationService";
import type { OnboardingAnswers, Question } from "../types/onboarding";

/**
 * Hook to manage question state and validation for onboarding
 */
export function useQuestionState(
	screens: Question[][],
	currentScreenIndex: number,
	answers: OnboardingAnswers,
) {
	const [showValidation, setShowValidation] = useState(false);
	const lastScreenIndexRef = useRef(currentScreenIndex);

	/**
	 * Triggers validation display for the current question
	 */
	const handleShowValidation = useCallback(() => {
		setShowValidation(true);
	}, []);

	/**
	 * Resets validation when screen changes
	 */
	const resetValidationOnScreenChange = useCallback(() => {
		if (lastScreenIndexRef.current !== currentScreenIndex) {
			setShowValidation(false);
			lastScreenIndexRef.current = currentScreenIndex;
		}
	}, [currentScreenIndex]);

	/**
	 * Gets the current question from screens
	 */
	const getCurrentQuestion = useCallback((): Question | null => {
		if (screens.length === 0 || currentScreenIndex >= screens.length) {
			return null;
		}
		return screens[currentScreenIndex][0];
	}, [screens, currentScreenIndex]);

	/**
	 * Checks if the current question is answered
	 */
	const isCurrentQuestionAnswered = useCallback((): boolean => {
		const question = getCurrentQuestion();
		if (!question) return false;

		return QuestionValidationService.validateQuestionCompletion(
			question,
			answers,
		);
	}, [getCurrentQuestion, answers]);

	/**
	 * Determines if we're on the last screen
	 */
	const isLastScreen = useCallback((): boolean => {
		return currentScreenIndex === screens.length - 1;
	}, [currentScreenIndex, screens.length]);

	/**
	 * Checks if validation should be triggered for current question
	 */
	const shouldTriggerValidation = useCallback((): boolean => {
		const question = getCurrentQuestion();
		if (!question) return false;

		return QuestionValidationService.shouldShowValidation(question);
	}, [getCurrentQuestion]);

	// Auto-clear validation when form becomes valid
	useEffect(() => {
		if (!showValidation || screens.length === 0) return;

		const question = getCurrentQuestion();
		if (!question) return;

		if (question.type === "personal-info") {
			const isValid = FormValidationService.hasPersonalInfoCompleted(answers);
			if (isValid) {
				setShowValidation(false);
			}
		}

		if (question.type === "setup-profile") {
			const isValid = FormValidationService.hasSetupProfileCompleted(answers);
			if (isValid) {
				setShowValidation(false);
			}
		}

		if (question.type === "setup-profile-details") {
			const isValid =
				FormValidationService.hasSetupProfileDetailsCompleted(answers);
			if (isValid) {
				setShowValidation(false);
			}
		}
	}, [answers, showValidation, screens.length, getCurrentQuestion]);

	// Reset validation on screen change
	useEffect(() => {
		resetValidationOnScreenChange();
	}, [resetValidationOnScreenChange]);

	return {
		// State
		showValidation,

		// Actions
		handleShowValidation,
		resetValidationOnScreenChange,

		// Computed values
		getCurrentQuestion,
		isCurrentQuestionAnswered,
		isLastScreen,
		shouldTriggerValidation,

		// Utils
		currentQuestion: getCurrentQuestion(),
		isAnswered: isCurrentQuestionAnswered(),
		isLast: isLastScreen(),
	};
}
